import { z } from 'zod';

// 'client' is only meaningful when FEATURE_CLIENTS_VIEW is enabled: read-only
// access limited to calls of the configurations assigned to the user.
export const userRoleSchema = z.enum(['admin', 'user', 'extension_only', 'client']);

export type UserRole = z.infer<typeof userRoleSchema>;

// User entity schema
export const userSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email(),
  role: userRoleSchema.default('user'),
  receive_notifications: z.boolean().default(true),
  // Configurations the user may access (clients view; role 'client' only)
  configuration_ids: z.array(z.string().uuid()).optional(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type User = z.infer<typeof userSchema>;

// Create user schema
export const createUserSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  role: userRoleSchema.default('user'),
  receive_notifications: z.boolean().optional(),
  configuration_ids: z.array(z.string().uuid()).optional(),
});

export type CreateUserDto = z.infer<typeof createUserSchema>;

// Update user schema
export const updateUserSchema = z.object({
  name: z.string().min(2).optional(),
  email: z.string().email().optional(),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  role: userRoleSchema.optional(),
  receive_notifications: z.boolean().optional(),
  configuration_ids: z.array(z.string().uuid()).optional(),
});

export type UpdateUserDto = z.infer<typeof updateUserSchema>;

// User list query schema
export const userQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  role: userRoleSchema.optional(),
  sortBy: z.enum(['created_at', 'updated_at', 'email', 'name', 'role']).default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type UserQuery = z.infer<typeof userQuerySchema>;
